import axios, { CancelToken } from 'axios';
import { getCommunityRoute } from '../../api/apiRoutes';
import { axiosAuthenticatedJsonRequest } from '../../api/axios';
import {
  AuthoringGovernedSurveyCatalogItem,
  EnhancedSurveyParent,
  EnhancedSurveyParentCreateRequest,
  EnhancedSurveyAnalysisSessionDetail,
  EnhancedSurveyAnalysisSessionPage,
  EnhancedSurveyAnalysisSessionQuery,
  EnhancedSurveyAnalysisWorkbookExport,
  EnhancedSurveyAnalysisExportQuery,
  EnhancedSurveyAdminRetakePreview,
  EnhancedSurveyAdminRetakeResult,
  GovernedSurveyDeletionRequest,
  GovernedSurveyDeletionResult,
  GovernedSurveyDuplicateCommand,
  GovernedSurveyMetadata,
  GovernedSurveyMetadataUpdate,
  GovernedSurveyExperienceSelection,
  GovernedSurveyMemberExperience,
  GovernedSurveySubmissionReceipt,
  GovernedSurveySubmissionRequest,
  PublishedGovernedSurveyCatalogItem,
  RoomOnboardingConfiguration,
  RoomOnboardingConfigurationUpdate,
  SurveyCodebook,
  SurveyCodebookApprovalCommand,
  SurveyCodebookConcurrencyCommand,
  SurveyCodebookCreateRequest,
  SurveyCodebookVersion,
  SurveyCodebookVersionCopyCommand,
  SurveyCodebookVersionDraftCreate,
  SurveyCodebookVersionDraftUpdate,
  SurveyGeneratedThemeCandidateReview,
  SurveyGeneratedThemePromotionRequest,
  SurveyBuilderCapabilities,
  SurveyBuilderCatalog,
  SurveyBuilderCatalogType,
  SurveyBuilderTemplateCreateRequest,
  SurveyBuilderTemplateSummary,
  SurveyConfigurationSelection,
  SurveyLanguageApprovalCommand,
  SurveyLifecycleAction,
  SurveyLifecycleCommand,
  SurveyResolutionResult,
  SurveyRecipientCandidate,
  SurveyReviewBatchCommand,
  SurveySourceReviewCommand,
  SurveySourceReviewQueue,
  SurveyTranslationDraftCommand,
  SurveyValidationError,
  SurveyValidationResult,
  SurveyVersion,
  SurveyVersionCopyCommand,
  SurveyVersionDraft,
  surveyBuilderCatalogTypes,
} from './models/GovernedSurvey';

const encodePathSegment = (value: string) => encodeURIComponent(value);
const governedRoomRoute = (roomId: string) =>
  `${getCommunityRoute()}/room/${encodePathSegment(roomId)}`;
const governedVersionRoute = (
  roomId: string,
  surveyId: string,
  version?: string
) => {
  const route = `${governedRoomRoute(roomId)}/survey/${encodePathSegment(
    surveyId
  )}/versions`;
  return version === undefined
    ? route
    : `${route}/${encodePathSegment(version)}`;
};

const governedCodebookRoute = (roomId: string, codebookId: string) =>
  `${governedRoomRoute(roomId)}/survey-authoring/codebooks/${encodePathSegment(
    codebookId
  )}`;

const governedCodebookVersionRoute = (
  roomId: string,
  codebookId: string,
  version: string
) =>
  `${governedCodebookRoute(roomId, codebookId)}/versions/${encodePathSegment(
    version
  )}`;

const generatedThemeCandidateRoute = (
  roomId: string,
  surveyId: string,
  surveyVersion: string,
  codebookId: string,
  codebookVersion: string
) =>
  `${governedVersionRoute(
    roomId,
    surveyId,
    surveyVersion
  )}/generated-theme-candidates/codebooks/${encodePathSegment(
    codebookId
  )}/versions/${encodePathSegment(codebookVersion)}`;

const catalogTypeValues = new Set<string>(
  Object.values(surveyBuilderCatalogTypes)
);
const lifecycleActions = new Set<SurveyLifecycleAction>([
  'submit',
  'reopen',
  'approve',
  'publish',
  'supersede',
  'retire',
]);

const isValidationResult = (
  value: unknown
): value is SurveyValidationResult => {
  if (!value || typeof value !== 'object' || !('errors' in value)) return false;
  return Array.isArray((value as SurveyValidationResult).errors);
};

export class GovernedSurveyApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly payload: unknown;
  readonly validationErrors: SurveyValidationError[];
  readonly errorCode?: string;
  readonly correlationId?: string;

  constructor(status: number, statusText: string, payload: unknown) {
    const validationErrors = isValidationResult(payload) ? payload.errors : [];
    const managedPayload = payload && typeof payload === 'object'
      ? payload as Record<string, unknown>
      : undefined;
    const managedMessage =
      validationErrors[0]?.message ||
      (typeof managedPayload?.exception_message === 'string'
        ? managedPayload.exception_message
        : undefined) ||
      (typeof payload === 'string' ? payload : statusText);
    super(managedMessage || 'Governed survey request failed');
    this.name = 'GovernedSurveyApiError';
    this.status = status;
    this.statusText = statusText;
    this.payload = payload;
    this.validationErrors = validationErrors;
    this.errorCode = typeof managedPayload?.error_code === 'string'
      ? managedPayload.error_code
      : undefined;
    this.correlationId = typeof managedPayload?.exception_correlationId === 'string'
      ? managedPayload.exception_correlationId
      : undefined;
  }
}

export const isGovernedRequestCancelled = (error: unknown) =>
  axios.isCancel(error);

export const normalizeGovernedSurveyError = (
  error: unknown
): GovernedSurveyApiError => {
  if (error instanceof GovernedSurveyApiError) return error;
  if (error && typeof error === 'object' && 'status' in error) {
    const response = error as {
      status?: number;
      statusText?: string;
      data?: unknown;
    };
    return new GovernedSurveyApiError(
      response.status ?? 700,
      response.statusText ?? '',
      response.data
    );
  }
  return new GovernedSurveyApiError(
    700,
    '',
    error instanceof Error ? error.message : error
  );
};

const request = async <TResponse, TBody = unknown>(options: {
  method: 'delete' | 'get' | 'patch' | 'post' | 'put';
  url: string;
  data?: TBody;
  cancelToken?: CancelToken;
  clientAction: string;
}): Promise<TResponse> => {
  try {
    return await axiosAuthenticatedJsonRequest<TResponse, TBody>(options);
  } catch (error) {
    if (isGovernedRequestCancelled(error)) throw error;
    throw normalizeGovernedSurveyError(error);
  }
};

export const getBuilderCapabilities = (roomId: string) =>
  request<SurveyBuilderCapabilities>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/survey-authoring/capabilities`,
    clientAction: 'governed-survey-capabilities',
  });

export const getBuilderTemplates = (roomId: string) =>
  request<SurveyBuilderTemplateSummary[]>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/survey-authoring/templates`,
    clientAction: 'governed-survey-templates',
  });

export const getSurveyRecipientCandidates = (roomId: string) =>
  request<SurveyRecipientCandidate[]>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/survey-authoring/recipient-candidates`,
    clientAction: 'governed-survey-recipient-candidates',
  });

export const getAuthoringGovernedSurveysForRoom = (roomId: string) =>
  request<AuthoringGovernedSurveyCatalogItem[]>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/governed-surveys/authoring`,
    clientAction: 'governed-survey-authoring-catalog',
  });

export const deleteGovernedSurvey = (
  roomId: string,
  surveyId: string,
  deletion: GovernedSurveyDeletionRequest
) =>
  request<GovernedSurveyDeletionResult, GovernedSurveyDeletionRequest>({
    method: 'delete',
    url: `${governedRoomRoute(roomId)}/governed-surveys/${encodePathSegment(
      surveyId
    )}`,
    data: deletion,
    clientAction: 'governed-survey-delete',
  });

export const deleteGovernedSurveyDraft = (
  roomId: string,
  surveyId: string,
  version: string
) =>
  request<void>({
    method: 'delete',
    url: governedVersionRoute(roomId, surveyId, version),
    clientAction: 'governed-survey-draft-delete',
  });

export const listSurveyCodebooks = (roomId: string) =>
  request<SurveyCodebook[]>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/survey-authoring/codebooks`,
    clientAction: 'governed-survey-codebooks',
  });

export const createSurveyCodebook = (
  roomId: string,
  command: SurveyCodebookCreateRequest
) =>
  request<SurveyCodebook>({
    method: 'post',
    url: `${governedRoomRoute(roomId)}/survey-authoring/codebooks`,
    data: command,
    clientAction: 'governed-survey-codebook-create',
  });

export const getSurveyCodebook = (roomId: string, codebookId: string) =>
  request<SurveyCodebook>({
    method: 'get',
    url: governedCodebookRoute(roomId, codebookId),
    clientAction: 'governed-survey-codebook',
  });

export const listSurveyCodebookVersions = (
  roomId: string,
  codebookId: string
) =>
  request<SurveyCodebookVersion[]>({
    method: 'get',
    url: `${governedRoomRoute(
      roomId
    )}/survey-authoring/codebooks/${encodePathSegment(codebookId)}/versions`,
    clientAction: 'governed-survey-codebook-versions',
  });

export const createSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  command: SurveyCodebookVersionDraftCreate
) =>
  request<SurveyCodebookVersion>({
    method: 'post',
    url: `${governedCodebookRoute(roomId, codebookId)}/versions`,
    data: command,
    clientAction: 'governed-survey-codebook-version-create',
  });

export const copySurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  command: SurveyCodebookVersionCopyCommand
) =>
  request<SurveyCodebookVersion>({
    method: 'post',
    url: `${governedCodebookRoute(roomId, codebookId)}/versions/from-version`,
    data: command,
    clientAction: 'governed-survey-codebook-version-copy',
  });

export const getSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string
) =>
  request<SurveyCodebookVersion>({
    method: 'get',
    url: governedCodebookVersionRoute(roomId, codebookId, version),
    clientAction: 'governed-survey-codebook-version',
  });

export const replaceSurveyCodebookVersionDraft = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookVersionDraftUpdate
) =>
  request<SurveyCodebookVersion>({
    method: 'put',
    url: governedCodebookVersionRoute(roomId, codebookId, version),
    data: command,
    clientAction: 'governed-survey-codebook-version-replace',
  });

const transitionSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  action: 'submit' | 'reopen' | 'publish' | 'supersede' | 'retire',
  command: SurveyCodebookConcurrencyCommand
) =>
  request<SurveyCodebookVersion>({
    method: 'post',
    url: `${governedCodebookVersionRoute(roomId, codebookId, version)}/${action}`,
    data: command,
    clientAction: `governed-survey-codebook-version-${action}`,
  });

export const submitSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookConcurrencyCommand
) => transitionSurveyCodebookVersion(roomId, codebookId, version, 'submit', command);

export const reopenSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookConcurrencyCommand
) => transitionSurveyCodebookVersion(roomId, codebookId, version, 'reopen', command);

export const approveSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookApprovalCommand
) =>
  request<SurveyCodebookVersion>({
    method: 'post',
    url: `${governedCodebookVersionRoute(roomId, codebookId, version)}/approve`,
    data: command,
    clientAction: 'governed-survey-codebook-version-approve',
  });

export const publishSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookConcurrencyCommand
) => transitionSurveyCodebookVersion(roomId, codebookId, version, 'publish', command);

export const supersedeSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookConcurrencyCommand
) =>
  transitionSurveyCodebookVersion(roomId, codebookId, version, 'supersede', command);

export const retireSurveyCodebookVersion = (
  roomId: string,
  codebookId: string,
  version: string,
  command: SurveyCodebookConcurrencyCommand
) => transitionSurveyCodebookVersion(roomId, codebookId, version, 'retire', command);

export const getSurveyGeneratedThemeCandidates = (
  roomId: string,
  surveyId: string,
  surveyVersion: string,
  codebookId: string,
  codebookVersion: string
) =>
  request<SurveyGeneratedThemeCandidateReview>({
    method: 'get',
    url: generatedThemeCandidateRoute(
      roomId,
      surveyId,
      surveyVersion,
      codebookId,
      codebookVersion
    ),
    clientAction: 'governed-survey-generated-theme-candidates',
  });

export const promoteSurveyGeneratedThemes = (
  roomId: string,
  surveyId: string,
  surveyVersion: string,
  codebookId: string,
  codebookVersion: string,
  command: SurveyGeneratedThemePromotionRequest
) =>
  request<SurveyCodebookVersion>({
    method: 'post',
    url: `${generatedThemeCandidateRoute(
      roomId,
      surveyId,
      surveyVersion,
      codebookId,
      codebookVersion
    )}/promote`,
    data: command,
    clientAction: 'governed-survey-generated-theme-promote',
  });

export const getPublishedGovernedSurveysForRoom = (roomId: string) =>
  request<PublishedGovernedSurveyCatalogItem[]>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/governed-surveys/published`,
    clientAction: 'governed-survey-published-catalog',
  });

export const getEnhancedSurveyAnalysisSessions = (
  roomId: string,
  surveyId: string,
  filters: EnhancedSurveyAnalysisSessionQuery = {},
  cancelToken?: CancelToken
) => {
  const query = new URLSearchParams();
  if (filters.surveyVersion) query.set('surveyVersion', filters.surveyVersion);
  if (filters.status && filters.status !== 'all') query.set('status', filters.status);
  if (filters.continuationToken) query.set('continuationToken', filters.continuationToken);
  if (filters.memberSearch) query.set('memberSearch', filters.memberSearch);
  const queryString = query.toString();

  return request<EnhancedSurveyAnalysisSessionPage>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/governed-surveys/${encodePathSegment(
      surveyId
    )}/analysis/sessions${queryString ? `?${queryString}` : ''}`,
    cancelToken,
    clientAction: 'enhanced-survey-analysis-sessions',
  });
};

export const getEnhancedSurveyAnalysisSession = (
  roomId: string,
  surveyId: string,
  sessionId: string,
  cancelToken?: CancelToken
) =>
  request<EnhancedSurveyAnalysisSessionDetail>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/governed-surveys/${encodePathSegment(
      surveyId
    )}/analysis/sessions/${encodePathSegment(sessionId)}`,
    cancelToken,
    clientAction: 'enhanced-survey-analysis-session',
  });

export const exportEnhancedSurveyAnalysisWorkbook = (
  roomId: string,
  surveyId: string,
  filters: EnhancedSurveyAnalysisExportQuery = {}
) => {
  const query = new URLSearchParams();
  if (filters.surveyVersion) query.set('surveyVersion', filters.surveyVersion);
  if (filters.status && filters.status !== 'all') query.set('status', filters.status);
  const queryString = query.toString();

  return request<EnhancedSurveyAnalysisWorkbookExport>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/governed-surveys/${encodePathSegment(
      surveyId
    )}/analysis/export${queryString ? `?${queryString}` : ''}`,
    clientAction: 'enhanced-survey-analysis-export-workbook',
  });
};

const enhancedSurveyRetakeRoute = (
  roomId: string,
  surveyId: string,
  sessionId: string
) =>
  `${governedRoomRoute(roomId)}/governed-surveys/${encodePathSegment(
    surveyId
  )}/analysis/sessions/${encodePathSegment(sessionId)}/retake`;

export const getEnhancedSurveyAdminRetakePreview = (
  roomId: string,
  surveyId: string,
  sessionId: string,
  cancelToken?: CancelToken
) =>
  request<EnhancedSurveyAdminRetakePreview>({
    method: 'get',
    url: enhancedSurveyRetakeRoute(roomId, surveyId, sessionId),
    cancelToken,
    clientAction: 'enhanced-survey-admin-retake-preview',
  });

export const resetEnhancedSurveyAdminRetake = (
  roomId: string,
  surveyId: string,
  sessionId: string
) =>
  request<EnhancedSurveyAdminRetakeResult>({
    method: 'post',
    url: enhancedSurveyRetakeRoute(roomId, surveyId, sessionId),
    clientAction: 'enhanced-survey-admin-retake-reset',
  });

export const getRoomOnboardingConfiguration = (roomId: string) =>
  request<RoomOnboardingConfiguration>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/aisettings/onboarding`,
    clientAction: 'room-onboarding-configuration',
  });

export const updateRoomOnboardingConfiguration = (
  roomId: string,
  update: RoomOnboardingConfigurationUpdate
) =>
  request<RoomOnboardingConfiguration, RoomOnboardingConfigurationUpdate>({
    method: 'put',
    url: `${governedRoomRoute(roomId)}/aisettings/onboarding`,
    data: update,
    clientAction: 'room-onboarding-configuration-update',
  });

export const getBuilderCatalog = (
  roomId: string,
  catalogType: SurveyBuilderCatalogType,
  search?: string,
  cancelToken?: CancelToken
) => {
  if (!catalogTypeValues.has(catalogType)) {
    throw new GovernedSurveyApiError(400, '', 'Unsupported catalog type');
  }
  if (search && search.length > 200) {
    throw new GovernedSurveyApiError(400, '', 'Catalog search is too long');
  }
  const searchQuery = search ? `?search=${encodeURIComponent(search)}` : '';
  return request<SurveyBuilderCatalog>({
    method: 'get',
    url: `${governedRoomRoute(
      roomId
    )}/survey-authoring/catalogs/${encodePathSegment(
      catalogType
    )}${searchQuery}`,
    cancelToken,
    clientAction: 'governed-survey-catalog',
  });
};

export const createEnhancedSurveyParent = (
  roomId: string,
  metadata: EnhancedSurveyParentCreateRequest
) =>
  request<EnhancedSurveyParent, EnhancedSurveyParentCreateRequest>({
    method: 'post',
    url: `${governedRoomRoute(roomId)}/survey`,
    data: metadata,
    clientAction: 'governed-survey-create-parent',
  });

export const listSurveyVersions = (roomId: string, surveyId: string) =>
  request<SurveyVersion[]>({
    method: 'get',
    url: governedVersionRoute(roomId, surveyId),
    clientAction: 'governed-survey-versions',
  });

export const getSurveyVersion = (
  roomId: string,
  surveyId: string,
  version: string
) =>
  request<SurveyVersion>({
    method: 'get',
    url: governedVersionRoute(roomId, surveyId, version),
    clientAction: 'governed-survey-version',
  });

export const getGovernedSurveyExperience = (
  roomId: string,
  surveyId: string,
  selection?: GovernedSurveyExperienceSelection
) => {
  const query = new URLSearchParams();
  if (selection?.language) query.set('language', selection.language);
  if (selection?.pathwayId) query.set('pathwayId', selection.pathwayId);
  if (selection?.wording) query.set('wording', selection.wording);
  const queryString = query.toString();

  return request<GovernedSurveyMemberExperience>({
    method: 'get',
    url: `${governedRoomRoute(roomId)}/survey/${encodePathSegment(
      surveyId
    )}/experience${queryString ? `?${queryString}` : ''}`,
    clientAction: 'governed-survey-member-experience',
  });
};

export const submitGovernedSurvey = (
  roomId: string,
  surveyId: string,
  submission: GovernedSurveySubmissionRequest
) =>
  request<GovernedSurveySubmissionReceipt, GovernedSurveySubmissionRequest>({
    method: 'post',
    url: `${governedRoomRoute(roomId)}/survey/${encodePathSegment(
      surveyId
    )}/submissions`,
    data: submission,
    clientAction: 'governed-survey-member-submission',
  });

export const createSurveyVersionFromTemplate = (
  roomId: string,
  surveyId: string,
  templateRequest: SurveyBuilderTemplateCreateRequest
) =>
  request<SurveyVersion, SurveyBuilderTemplateCreateRequest>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId)}/from-template`,
    data: templateRequest,
    clientAction: 'governed-survey-create-from-template',
  });

export const copySurveyVersionToDraft = (
  roomId: string,
  surveyId: string,
  sourceVersion: string,
  command: SurveyVersionCopyCommand
) =>
  request<SurveyVersion, SurveyVersionCopyCommand>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, sourceVersion)}/copy`,
    data: command,
    clientAction: 'governed-survey-copy-version',
  });

export const duplicateGovernedSurvey = (
  roomId: string,
  surveyId: string,
  command: GovernedSurveyDuplicateCommand
) =>
  request<EnhancedSurveyParent, GovernedSurveyDuplicateCommand>({
    method: 'post',
    url: `${governedRoomRoute(roomId)}/survey/${encodePathSegment(
      surveyId
    )}/duplicate`,
    data: command,
    clientAction: 'governed-survey-duplicate',
  });

export const updateGovernedSurveyMetadata = (
  roomId: string,
  surveyId: string,
  metadata: GovernedSurveyMetadataUpdate
) =>
  request<GovernedSurveyMetadata, GovernedSurveyMetadataUpdate>({
    method: 'patch',
    url: `${governedRoomRoute(roomId)}/governed-surveys/${encodePathSegment(
      surveyId
    )}`,
    data: metadata,
    clientAction: 'governed-survey-update-metadata',
  });

export const createSurveyVersionDraft = (
  roomId: string,
  surveyId: string,
  draft: Omit<SurveyVersionDraft, 'concurrencyToken'>,
  importDefinition = false
) =>
  request<SurveyVersion, Omit<SurveyVersionDraft, 'concurrencyToken'>>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId)}${
      importDefinition ? '/import' : ''
    }`,
    data: draft,
    clientAction: importDefinition
      ? 'governed-survey-import-draft'
      : 'governed-survey-create-draft',
  });

export const replaceSurveyVersionDraft = (
  roomId: string,
  surveyId: string,
  version: string,
  snapshot: SurveyVersionDraft
) =>
  request<SurveyVersion, SurveyVersionDraft>({
    method: 'put',
    url: governedVersionRoute(roomId, surveyId, version),
    data: snapshot,
    clientAction: 'governed-survey-save-draft',
  });

export const validateSurveyVersion = (
  roomId: string,
  surveyId: string,
  version: string
) =>
  request<SurveyValidationResult>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/validate`,
    clientAction: 'governed-survey-validate',
  });

export const previewSurveyVersion = (
  roomId: string,
  surveyId: string,
  version: string,
  selection: SurveyConfigurationSelection
) =>
  request<SurveyResolutionResult, SurveyConfigurationSelection>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/effective-preview`,
    data: selection,
    clientAction: 'governed-survey-preview',
  });

export const createTranslationDraft = (
  roomId: string,
  surveyId: string,
  version: string,
  command: SurveyTranslationDraftCommand
) =>
  request<SurveyVersion, SurveyTranslationDraftCommand>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/translation-draft`,
    data: command,
    clientAction: 'governed-survey-translation-draft',
  });

export const setLanguageApproval = (
  roomId: string,
  surveyId: string,
  version: string,
  command: SurveyLanguageApprovalCommand
) =>
  request<SurveyVersion, SurveyLanguageApprovalCommand>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/language-approval`,
    data: command,
    clientAction: 'governed-survey-language-approval',
  });

export const getSourceReviews = (
  roomId: string,
  surveyId: string,
  version: string
) =>
  request<SurveySourceReviewQueue>({
    method: 'get',
    url: `${governedVersionRoute(roomId, surveyId, version)}/source-reviews`,
    clientAction: 'governed-survey-source-reviews',
  });

export const setReviewBatch = (
  roomId: string,
  surveyId: string,
  version: string,
  command: SurveyReviewBatchCommand
) =>
  request<SurveyVersion, SurveyReviewBatchCommand>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/review-batch`,
    data: command,
    clientAction: 'governed-survey-review-batch',
  });

export const setSourceReview = (
  roomId: string,
  surveyId: string,
  version: string,
  command: SurveySourceReviewCommand
) =>
  request<SurveyVersion, SurveySourceReviewCommand>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/source-review`,
    data: command,
    clientAction: 'governed-survey-source-review',
  });

export const transitionSurveyVersion = (
  roomId: string,
  surveyId: string,
  version: string,
  action: SurveyLifecycleAction,
  command: SurveyLifecycleCommand
) => {
  if (!lifecycleActions.has(action)) {
    throw new GovernedSurveyApiError(400, '', 'Unsupported lifecycle action');
  }
  if (action === 'approve') {
    const approvalVersion = command.approvalVersion;
    if (!approvalVersion?.trim()) {
      throw new GovernedSurveyApiError(400, '', 'Approval version is required');
    }
    const data = {
      concurrencyToken: command.concurrencyToken,
      approvalVersion,
    };
    return request<SurveyVersion, typeof data>({
      method: 'post',
      url: `${governedVersionRoute(roomId, surveyId, version)}/${action}`,
      data,
      clientAction: `governed-survey-${action}`,
    });
  }
  if (action === 'publish') {
    const data = command.currentPublishedVersion
      ? {
          concurrencyToken: command.concurrencyToken,
          currentPublishedVersion: command.currentPublishedVersion,
          currentPublishedConcurrencyToken:
            command.currentPublishedConcurrencyToken,
        }
      : { concurrencyToken: command.concurrencyToken };
    return request<SurveyVersion, typeof data>({
      method: 'post',
      url: `${governedVersionRoute(roomId, surveyId, version)}/${action}`,
      data,
      clientAction: `governed-survey-${action}`,
    });
  }
  const data = { concurrencyToken: command.concurrencyToken };
  return request<SurveyVersion, typeof data>({
    method: 'post',
    url: `${governedVersionRoute(roomId, surveyId, version)}/${action}`,
    data,
    clientAction: `governed-survey-${action}`,
  });
};
